import type { AuditActionType, BadgeColor, NotificationCategory, SearchResultType } from "@/services/types";

export const AUDIT_MODULES = [
  "Collection",
  "Payout",
  "Auction",
  "Members",
  "Groups",
  "Ledger",
  "Settings",
];

export interface AuditActionMeta {
  label: string;
  badge: BadgeColor;
}

export const AUDIT_ACTION_META: Record<AuditActionType, AuditActionMeta> = {
  create: { label: "Created", badge: "success" },
  update: { label: "Updated", badge: "info" },
  delete: { label: "Deleted", badge: "danger" },
  activate: { label: "Activated", badge: "success" },
  enroll: { label: "Enrolled", badge: "primary" },
  remove: { label: "Removed", badge: "warning" },
  record_bid: { label: "Bid recorded", badge: "primary" },
  withdraw_bid: { label: "Bid withdrawn", badge: "warning" },
  close_auction: { label: "Auction closed", badge: "primary" },
  record_receipt: { label: "Receipt recorded", badge: "success" },
  reverse_receipt: { label: "Receipt reversed", badge: "danger" },
  apply_late_fee: { label: "Late fee applied", badge: "warning" },
  waive_late_fee: { label: "Late fee waived", badge: "accent" },
  approve_payout: { label: "Payout approved", badge: "success" },
  disburse_payout: { label: "Payout disbursed", badge: "success" },
  reverse_payout: { label: "Payout reversed", badge: "danger" },
  ledger_adjust: { label: "Ledger adjusted", badge: "info" },
  create_user: { label: "User created", badge: "success" },
  update_user: { label: "User updated", badge: "info" },
  deactivate_user: { label: "User deactivated", badge: "danger" },
  reset_password: { label: "Password reset", badge: "info" },
  update_setting: { label: "Setting changed", badge: "accent" },
};

export function getAuditActionMeta(action: string): AuditActionMeta {
  const norm = String(action || "").toLowerCase();
  const map: Record<string, AuditActionMeta> = {
    create: { label: "Created", badge: "success" },
    user_create: { label: "User created", badge: "success" },
    update: { label: "Updated", badge: "info" },
    user_update: { label: "User updated", badge: "info" },
    user_status_change: { label: "Status changed", badge: "info" },
    settings_update: { label: "Setting changed", badge: "accent" },
    update_setting: { label: "Setting changed", badge: "accent" },
    numbering_update: { label: "Numbering updated", badge: "accent" },
    delete: { label: "Deleted", badge: "danger" },
    activate: { label: "Activated", badge: "success" },
    enroll: { label: "Enrolled", badge: "primary" },
    remove: { label: "Removed", badge: "warning" },
    record_bid: { label: "Bid recorded", badge: "primary" },
    withdraw_bid: { label: "Bid withdrawn", badge: "warning" },
    close_auction: { label: "Auction closed", badge: "primary" },
    record_receipt: { label: "Receipt recorded", badge: "success" },
    reverse_receipt: { label: "Receipt reversed", badge: "danger" },
    apply_late_fee: { label: "Late fee applied", badge: "warning" },
    waive_late_fee: { label: "Late fee waived", badge: "accent" },
    approve_payout: { label: "Payout approved", badge: "success" },
    disburse_payout: { label: "Payout disbursed", badge: "success" },
    reverse_payout: { label: "Payout reversed", badge: "danger" },
    ledger_adjust: { label: "Ledger adjusted", badge: "info" },
    create_user: { label: "User created", badge: "success" },
    deactivate_user: { label: "User deactivated", badge: "danger" },
    reset_password: { label: "Password reset", badge: "info" },
    password_reset: { label: "Password reset", badge: "info" },
  };

  return map[norm] || AUDIT_ACTION_META[action as AuditActionType] || { label: String(action), badge: "info" };
}

export interface NotificationMeta {
  label: string;
  icon: string;
  color: BadgeColor;
}

export const NOTIFICATION_META: Record<NotificationCategory, NotificationMeta> = {
  auction_due: { label: "Auction due", icon: "ri-hammer-line", color: "primary" },
  auction_overdue: { label: "Auction overdue", icon: "ri-alarm-warning-line", color: "danger" },
  collection_overdue: { label: "Collection overdue", icon: "ri-wallet-3-line", color: "warning" },
  payout_approval: { label: "Payout approval", icon: "ri-bank-line", color: "accent" },
  demand_overdue: { label: "Demand overdue", icon: "ri-time-line", color: "danger" },
};

export interface SearchTypeMeta {
  label: string;
  icon: string;
}

export const SEARCH_TYPE_META: Record<SearchResultType, SearchTypeMeta> = {
  member: { label: "Members", icon: "ri-group-line" },
  group: { label: "Groups", icon: "ri-archive-line" },
  receipt: { label: "Receipts", icon: "ri-receipt-line" },
  voucher: { label: "Payout vouchers", icon: "ri-bank-line" },
};